// Third-party packages
import {
	RUNTIME_CONFIG,
} from '@appConf/runtime.conf';
import i18n from '@modules/i18n';
import {
	QueryClient,
	QueryClientProvider,
} from '@tanstack/react-query';
import {
	ReactQueryDevtools,
} from '@tanstack/react-query-devtools';
import {
	ReactNode,
	StrictMode,
	Suspense,
	useEffect,
} from 'react';
import {
	CookiesProvider,
} from 'react-cookie';
import {
	createRoot,
} from 'react-dom/client';
import {
	I18nextProvider,
} from 'react-i18next';
import {
	Provider,
} from 'react-redux';
import {
	BrowserRouter as Router,
} from 'react-router-dom';

// Internal libraries (@stonal-tech/*)
import {
	ThemeProvider,
} from '@stonal-tech/lib-design-system-react';

// Project modules
import {
	store,
} from '@stores/lpdipro/store';

// Templates / Layouts / Pages
import {
	LpdiProApp,
} from '@layouts/_App/lpdipro';

// Simple wrapper for future extensibility (no visual loading indicator)
const NonBlockingPersistWrapper = ({
	children,
}: { children: ReactNode }) => {
	useEffect(() => {
		// Optional: Log when persistence is complete (dev only)
		if (process.env.NODE_ENV === 'development') {
			const unsubscribe = store.subscribe(() => {
			});
			return unsubscribe;
		}
	}, []);

	return <>{children}</>;
};

const AppProviders = ({
	children,
}: { children: ReactNode }) => (
	<ThemeProvider theme='light'>
		<Provider store={store}>
			<NonBlockingPersistWrapper>
				<CookiesProvider>
					<Suspense fallback={null}>
						{children}
					</Suspense>
				</CookiesProvider>
			</NonBlockingPersistWrapper>
		</Provider>
	</ThemeProvider>
);

const App = () => {
	const queryClient = new QueryClient();
	const basename = RUNTIME_CONFIG.MODE === 'development' ? '/webapp' : '/app/webapp';
	return (
		<StrictMode>
			<AppProviders>
				<I18nextProvider i18n={i18n}>
					<QueryClientProvider client={queryClient}>
						<Router basename={basename}>
							<LpdiProApp />
							<ReactQueryDevtools initialIsOpen={false} />
						</Router>
					</QueryClientProvider>
				</I18nextProvider>
			</AppProviders>
		</StrictMode>
	);
};

const container = document.getElementById('root');
if (container) {
	const root = createRoot(container);
	root.render(<App />);
} else {
	document.body.innerHTML = '<div style="padding: 20px;">No root element found</div>';
}
